import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import clsx from 'clsx';
import { MapPin, MessageSquare } from 'lucide-react';
import { clientsAPI, usersAPI } from '@shared/apiClient';
import { useMessageThreads } from '@shared/messaging/useMessaging';
import Dock from '../../shell/Dock';
import { useShell } from '../../shell/ShellContext';
import { useDispatchWorkspace } from './DispatchContext';
import { useDragAssign } from '../../hooks/useDragAssign';
import { buildTechColorMap, getPreferredTechnicianIds, technicianName } from './constants';
import AssignmentCard from './components/AssignmentCard';

export default function TechDock() {
  const [dragOverTechId, setDragOverTechId] = useState(null);
  const { openMessageThread } = useShell();
  const {
    draggedItem,
    draggedAssignment,
    setDraggedAssignment,
    clearDrag,
    setSelectedDetail,
    focusedTechId,
    setFocusedTechId,
  } = useDispatchWorkspace();
  const { assignBacklogToTech, reassignAssignment } = useDragAssign();

  const { data: techniciansData } = useQuery(
    'technicians',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 })
  );

  const { data: weeklyAssignmentsData } = useQuery(
    'weeklyAssignments',
    () => clientsAPI.getWeeklyAssignments(),
    { refetchInterval: 30000 }
  );

  const { data: messageThreads = [] } = useMessageThreads({
    refetchInterval: 30000,
  });

  const unreadByTechId = useMemo(() => {
    const map = {};
    messageThreads.forEach((thread) => {
      map[thread.technicianId] = thread.unreadCount || 0;
    });
    return map;
  }, [messageThreads]);

  const technicians = useMemo(() => {
    const all = (techniciansData?.data?.users || []).filter((t) => t.isActive);
    const preferred = getPreferredTechnicianIds();
    if (preferred.length === 0) return all;
    const preferredSet = new Set(preferred);
    const ordered = preferred.map((id) => all.find((t) => t.id === id)).filter(Boolean);
    const rest = all.filter((t) => !preferredSet.has(t.id));
    return [...ordered, ...rest];
  }, [techniciansData]);

  const techColorMap = useMemo(() => buildTechColorMap(technicians), [technicians]);

  const assignmentsByTechnician = useMemo(() => {
    const assignments = weeklyAssignmentsData?.data?.assignments || [];
    const grouped = {};
    assignments.forEach((assignment) => {
      const techId = assignment.technicianId;
      if (!grouped[techId]) grouped[techId] = [];
      grouped[techId].push(assignment);
    });
    return grouped;
  }, [weeklyAssignmentsData]);

  const totalAssignments = weeklyAssignmentsData?.data?.assignments?.length ?? 0;

  const handleDrop = async (e, technicianId) => {
    e.preventDefault();
    setDragOverTechId(null);

    if (draggedAssignment) {
      const assignment = draggedAssignment;
      clearDrag();
      if (assignment.technicianId === technicianId) return;
      reassignAssignment.mutate({ assignment, technicianId });
      return;
    }

    if (draggedItem) {
      const item = draggedItem;
      clearDrag();
      assignBacklogToTech.mutate({ item, technicianId });
    }
  };

  return (
    <Dock
      id="tech-dock"
      side="bottom"
      title="Technicians"
      badge={totalAssignments}
      defaultSize={180}
      minSize={120}
      maxSize={320}
      defaultCollapsed
      collapsedLabel={totalAssignments}
      dataTourId="tech-dock"
    >
      <div className="h-full overflow-x-auto overflow-y-hidden">
        <div className="flex gap-2 p-2 h-full min-w-max">
          {technicians.map((tech) => {
            const assignments = assignmentsByTechnician[tech.id] || [];
            const color = techColorMap[tech.id];
            const isDropTarget = dragOverTechId === tech.id;
            const isFocused = focusedTechId === tech.id;
            const unreadCount = unreadByTechId[tech.id] || 0;

            return (
              <div
                key={tech.id}
                className={clsx(
                  'flex-shrink-0 w-44 flex flex-col rounded border bg-surface',
                  isDropTarget && 'hub-drop-target',
                  isFocused && 'ring-2 ring-primary-400',
                  'border-surface-border'
                )}
                style={{ borderTopWidth: 3, borderTopColor: color }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverTechId(tech.id);
                }}
                onDragLeave={() => setDragOverTechId(null)}
                onDrop={(e) => handleDrop(e, tech.id)}
              >
                <div className="flex-shrink-0 px-2 py-1.5 border-b border-surface-border flex items-center justify-between gap-1">
                  <span className="text-xs font-semibold truncate">{technicianName(tech)}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openMessageThread({ technicianId: tech.id, technician: tech })}
                      className={clsx(
                        'p-0.5 rounded relative',
                        unreadCount > 0 ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600'
                      )}
                      title={unreadCount > 0 ? `Message ${technicianName(tech)} (${unreadCount} unread)` : `Message ${technicianName(tech)}`}
                    >
                      <MessageSquare className="h-3 w-3" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[0.75rem] h-3 px-0.5 rounded-full bg-primary-600 text-white text-[9px] font-semibold leading-none flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFocusedTechId(isFocused ? '' : tech.id)}
                      className={clsx(
                        'p-0.5 rounded',
                        isFocused ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600'
                      )}
                      title="Show on map"
                    >
                      <MapPin className="h-3 w-3" />
                    </button>
                    <span className="text-2xs tabular-nums font-semibold">{assignments.length}</span>
                  </div>
                </div>
                <div className="flex-1 overflow-auto p-1 space-y-1 max-h-28">
                  {assignments.slice(0, 6).map((assignment) => (
                    <AssignmentCard
                      key={assignment.id}
                      assignment={assignment}
                      compact
                      isDragging={draggedAssignment?.id === assignment.id}
                      onDragStart={setDraggedAssignment}
                      onDragEnd={clearDrag}
                      onClick={(a) => setSelectedDetail({ type: 'assignment', assignment: a })}
                    />
                  ))}
                  {assignments.length > 6 && (
                    <div className="text-2xs text-gray-400 text-center">+{assignments.length - 6} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Dock>
  );
}
